import type { ErrorEvent } from '@sentry/react';

/**
 * A filename pointing at a real script file — `.js`, `.mjs`, `.cjs`, and the `.ts`/`.tsx`
 * variants Vite serves unbundled in dev — ignoring any query or hash suffix.
 */
const SCRIPT_FILENAME = /\.[cm]?[jt]sx?([?#]|$)/;

const isScriptFilename = (filename: string | undefined): boolean =>
  filename !== undefined && SCRIPT_FILENAME.test(filename);

/**
 * True for an exception carrying no frame from a script we ship.
 *
 * Code injected into the page by an in-app WebView or a browser extension throws with frames
 * that name no script at all: the filename is either the document URL (`.../chat`, at a line
 * number far past the end of our 9-line `index.html`) or the literal string `undefined`,
 * because the injected source has no URL of its own. Its messages arrive minified to bare
 * identifiers — `fa`, `Aa` — so matching them via `ignoreErrors` holds only until the host
 * browser ships a new build and the mangled names change.
 *
 * Anything thrown by our own code has at least one frame in a real script file, so the absence
 * of one is the durable signal. Checked across every `exception.values` entry so a `cause` chain
 * whose outer link lost its stacktrace still counts as ours.
 *
 * Frameless exceptions count as foreign too: an exception with no stacktrace — an opaque
 * cross-origin `Script error.`, or the unhandled rejections this same injected code raises —
 * can't be actioned. Non-exception events such as `captureMessage` are left alone.
 *
 * Extension errors that *do* name a script (`chrome-extension://…/inject.js`) still reach
 * Sentry; `ignoreErrors` and `denyUrls` remain the tool for those.
 *
 * Seen as PATIENT-WEB-5E, 5C, 5A (`Error: fa`), 4S (`Error: Aa`) and 4T (`RangeError` at
 * `undefined:185:70`) — 15 events, every one from an iOS in-app WebView, none with an app frame.
 */
export const isForeignScriptError = (event: ErrorEvent): boolean => {
  const values = event.exception?.values;
  if (!values?.length) {
    return false;
  }
  const frames = values.flatMap((value) => value.stacktrace?.frames ?? []);
  return !frames.some((frame) => isScriptFilename(frame.filename));
};
